import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../../utils/helpers";
import { styles } from "./adminStyles";
import HasSourceConfig from "./ak/HasSourceConfig";
import AkModesManager from "./ak/AkModesManager";
import SarrafiyeCarpanTable from "./ak/SarrafiyeCarpanTable";
import MilyemTable from "./ak/MilyemTable";
import MakasModeCard from "./MakasModeCard";

const SARRAFIYE = [
  { key: "ceyrek", label: "Çeyrek" },
  { key: "yarim", label: "Yarım" },
  { key: "teklik", label: "Teklik" },
  { key: "ata_lira", label: "Ata Lira" },
  { key: "gremse", label: "Gremse" },
];

const MILYEM_PRODUCTS = [
  { key: "bilezik", label: "22 Ayar (Bilezik)", onlyAlis: true, singleMilyem: true },
  { key: "gram_1", label: "1 Gram" },
  { key: "gram_5", label: "5 Gram" },
  { key: "gram_10", label: "10 Gram" },
  { key: "gram_20", label: "20 Gram" },
];

const STATUS_BADGE = {
  verified: { color: "#22c55e", label: "Doğrulandı" },
  suspicious: { color: "#f59e0b", label: "Şüpheli" },
  failed: { color: "#ef4444", label: "Başarısız" },
  default: { color: "#6b7280", label: "Bekleniyor" },
  manual: { color: "#3b82f6", label: "Manuel" },
};

const PRICING_MODE = "altinkaynak_carpan";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // Backend Turkey wall-clock'u "+00:00" etiketli kaydediyor (legacy).
    // getUTCHours/getUTCMinutes ile saatler ham/saved degerleri okur (bolge sapmasi yok).
    return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  } catch { return "—"; }
};
const fmtCountdown = (sec) => {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
};

const AltinkaynakCarpanTab = ({ profile, pricingModeActive, onActivate }) => {
  const makasRef = useRef(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [carpan, setCarpan] = useState(null);
  const [milyem, setMilyem] = useState(null);
  const [health, setHealth] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [hasSource, setHasSource] = useState("altinkaynak");
  const [fallback, setFallback] = useState({ active: false, seconds_left: 0, reason: null });
  const [divergenceMode, setDivergenceMode] = useState("fallback");
  const [autoMarjAdj, setAutoMarjAdj] = useState({ alis: 0, satis: 0, at: null });
  const [lastDivergence, setLastDivergence] = useState({ diff_alis: 0, diff_satis: 0, at: null });
  const [ozanHealth, setOzanHealth] = useState({ status: "unknown" });
  const [aksoStatus, setAksoStatus] = useState(null);

  // Sub-mode (Mod 1, Mod 2, Pazar vs)
  const [modes, setModes] = useState([]);
  const [activeMode, setActiveMode] = useState("mod1");
  const [editingMode, setEditingMode] = useState("mod1");
  const [renamingMode, setRenamingMode] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const showMessage = useCallback((kind, msg) => {
    if (kind === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 3500); }
    else { setError(msg); setTimeout(() => setError(""), 4000); }
  }, []);

  const loadModes = useCallback(async () => {
    try {
      const [m, a, s] = await Promise.all([
        axios.get(`${API}/admin/modes?profile=${profile}&pricing_mode=${PRICING_MODE}`),
        axios.get(`${API}/admin/active-mode?profile=${profile}&pricing_mode=${PRICING_MODE}`),
        axios.get(`${API}/admin/ak-carpan/settings?profile=${profile}`),
      ]);
      const list = m.data.modes || [{ id: "mod1", name: "Mod 1" }];
      setModes(list);
      const am = a.data.mode || list[0]?.id || "mod1";
      setActiveMode(am);
      setEditingMode(am);
      setHasSource(s.data.has_source || "altinkaynak");
      setFallback(s.data.fallback || { active: false });
      setDivergenceMode(s.data.divergence_mode || "fallback");
      setAutoMarjAdj(s.data.auto_marj_adjustment || { alis: 0, satis: 0, at: null });
      setLastDivergence(s.data.last_divergence || { diff_alis: 0, diff_satis: 0, at: null });
      setOzanHealth(s.data.ozan_health || { status: "unknown" });
      setAksoStatus(s.data.akso_status || null);
    } catch (err) {
      console.error("AK loadModes error:", err);
      setModes([{ id: "mod1", name: "Mod 1" }]);
    }
  }, [profile]);

  const loadData = useCallback(async (mode) => {
    if (!mode) return;
    setLoading(true);
    try {
      const [c, m, h] = await Promise.all([
        axios.get(`${API}/admin/ak-carpan/sarrafiye?profile=${profile}&marj_mode=${mode}`),
        axios.get(`${API}/admin/ak-carpan/milyem?profile=${profile}&marj_mode=${mode}`),
        axios.get(`${API}/admin/ak-carpan/health?profile=${profile}&marj_mode=${mode}`),
      ]);
      setCarpan(c.data);
      setMilyem(m.data);
      setHealth(h.data);
      setCountdown(h.data.seconds_to_next);
    } catch (err) {
      console.error("AK loadData error:", err);
      showMessage("error", "Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [profile, showMessage]);

  useEffect(() => { loadModes(); }, [loadModes]);
  useEffect(() => { if (editingMode) loadData(editingMode); }, [editingMode, loadData]);

  useEffect(() => {
    if (countdown == null || countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  // Auto-refresh settings (fallback + divergence + auto-marj adjustment) every 10 sec
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const s = await axios.get(`${API}/admin/ak-carpan/settings?profile=${profile}`);
        setFallback(s.data.fallback || { active: false });
        setDivergenceMode(s.data.divergence_mode || "fallback");
        setAutoMarjAdj(s.data.auto_marj_adjustment || { alis: 0, satis: 0, at: null });
        setLastDivergence(s.data.last_divergence || { diff_alis: 0, diff_satis: 0, at: null });
        setOzanHealth(s.data.ozan_health || { status: "unknown" });
        setAksoStatus(s.data.akso_status || null);
        // Healthy degerlerini de yenile (son guncelleme, kalan sure vs.)
        if (editingMode) {
          const h = await axios.get(`${API}/admin/ak-carpan/health?profile=${profile}&marj_mode=${editingMode}`);
          setHealth(h.data);
          setCountdown(h.data.seconds_to_next);
        }
      } catch (err) {
        console.error("AK settings refresh error:", err);
      }
    }, 10000);
    return () => clearInterval(t);
  }, [profile, editingMode]);

  const updateCarpanField = (key, field, value) => {
    setCarpan(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };
  const updateHasField = (field, value) => {
    setCarpan(prev => ({ ...prev, has_altin: { ...(prev.has_altin || {}), [field]: value } }));
  };
  const updateMilyemField = (key, field, value) => {
    setMilyem(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };
  const setFormulaType = (ft) => {
    setCarpan(prev => ({ ...prev, formula_type: ft }));
  };

  const toggleMode = async (newMode) => {
    try {
      await axios.post(`${API}/admin/ak-carpan/mode?profile=${profile}&marj_mode=${editingMode}`, { mode: newMode });
      setCarpan(prev => ({ ...prev, mode: newMode }));
      showMessage("success", newMode === "auto" ? "Otomatik mod aktif" : "Manuel mod aktif");
    } catch (err) { console.error("AK toggleMode error:", err); showMessage("error", "Mod değiştirilemedi"); }
  };

  const parseNum = (v) => {
    const s = String(v ?? "").replace(",", ".");
    return s === "" || s === "-" ? 0 : parseFloat(s) || 0;
  };

  const saveCarpan = async () => {
    try {
      const payload = { mode: carpan.mode, formula_type: carpan.formula_type || "carpan" };
      SARRAFIYE.forEach(s => {
        const c = carpan[s.key] || {};
        payload[s.key] = {
          alis_carpan: parseNum(c.alis_carpan), satis_carpan: parseNum(c.satis_carpan),
          alis_marj: parseNum(c.alis_marj), satis_marj: parseNum(c.satis_marj),
          alis_iscilik: parseNum(c.alis_iscilik), satis_iscilik: parseNum(c.satis_iscilik),
        };
      });
      payload.has_altin = {
        alis_marj: parseNum(carpan.has_altin?.alis_marj),
        satis_marj: parseNum(carpan.has_altin?.satis_marj),
      };
      await axios.post(`${API}/admin/ak-carpan/sarrafiye/save?profile=${profile}&marj_mode=${editingMode}`, payload);
      showMessage("success", "Çarpan ayarları kaydedildi");
      loadData(editingMode);
    } catch (err) { console.error("AK saveCarpan error:", err); showMessage("error", "Kaydetme hatası"); }
  };

  const saveHasOnly = async () => {
    try {
      const payload = {
        has_altin: {
          alis_marj: parseNum(carpan.has_altin?.alis_marj),
          satis_marj: parseNum(carpan.has_altin?.satis_marj),
        }
      };
      await axios.post(`${API}/admin/ak-carpan/sarrafiye/save?profile=${profile}&marj_mode=${editingMode}`, payload);
      showMessage("success", "HAS altın marjı kaydedildi");
    } catch (err) { console.error("AK saveHasOnly error:", err); showMessage("error", "Kaydetme hatası"); }
  };

  const saveMilyem = async () => {
    try {
      const payload = {};
      MILYEM_PRODUCTS.forEach(p => {
        const m = milyem[p.key] || {};
        if (p.singleMilyem) {
          payload[p.key] = {
            milyem: parseNum(m.milyem),
            alis_marj: parseNum(m.alis_marj),
          };
        } else {
          payload[p.key] = {
            alis_milyem: parseNum(m.alis_milyem ?? m.milyem),
            satis_milyem: parseNum(m.satis_milyem ?? m.milyem),
            alis_marj: parseNum(m.alis_marj),
            satis_marj: parseNum(m.satis_marj),
          };
        }
      });
      await axios.post(`${API}/admin/ak-carpan/milyem/save?profile=${profile}&marj_mode=${editingMode}`, payload);
      showMessage("success", "Milyem ayarları kaydedildi");
    } catch (err) { console.error("AK saveMilyem error:", err); showMessage("error", "Kaydetme hatası"); }
  };

  const saveAll = async () => {
    try {
      // Carpan + Milyem + Makas kaydetme islemlerini paralel yap
      await Promise.all([saveCarpan(), saveMilyem(), makasRef.current?.save()]);
      showMessage("success", "Tüm ayarlar kaydedildi");
    } catch (err) { console.error("AK saveAll error:", err); showMessage("error", "Toplu kayıt hatası"); }
  };

  const refreshNow = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/admin/ak-carpan/refresh-now?profile=${profile}&marj_mode=${editingMode}`);
      if (res.data.success) {
        showMessage("success", res.data.message || "Çarpanlar güncellendi");
        loadData(editingMode);
      } else {
        showMessage("error", res.data.message || "Güncelleme başarısız");
      }
    } catch (e) {
      showMessage("error", e.response?.data?.detail || "Güncelleme hatası");
    } finally {
      setRefreshing(false);
    }
  };

  const activate = async () => {
    try {
      await axios.post(`${API}/admin/pricing-mode/activate?profile=${profile}`, { mode: PRICING_MODE });
      showMessage("success", "Yeni Altınkaynak modu aktif edildi!");
      if (onActivate) onActivate(PRICING_MODE);
    } catch (err) { console.error("AK activate error:", err); showMessage("error", "Aktif edilemedi"); }
  };

  const changeHasSource = async (src) => {
    if (src === hasSource) return;
    try {
      await axios.post(`${API}/admin/ak-carpan/settings?profile=${profile}`, { has_source: src });
      setHasSource(src);
      showMessage("success", src === "ozan" ? "HAS kaynağı: Ozan (3sn)" : "HAS kaynağı: Altınkaynak (1sn)");
    } catch (err) { console.error("AK changeHasSource error:", err); showMessage("error", "Kaynak değiştirilemedi"); }
  };

  const changeDivergenceMode = async (dm) => {
    if (dm === divergenceMode) return;
    try {
      await axios.post(`${API}/admin/ak-carpan/settings?profile=${profile}`, { divergence_mode: dm });
      setDivergenceMode(dm);
      showMessage("success",
        dm === "off" ? "Sapma Modu: ⛔ Kapalı (Ozan'a istek yok)"
        : dm === "auto_marj" ? "Sapma Modu: ⚙️ Oto Marj (HAS marjları otomatik ayarlanacak)"
        : "Sapma Modu: 🛡️ Fallback (5 TL fark → 10 dk Ozan)"
      );
      // 5 sn sonra settings'i yeniden yukle (yeni adjustment'lari almak icin)
      setTimeout(() => loadModes(), 5000);
    } catch (err) { console.error("AK changeDivergenceMode error:", err); showMessage("error", "Sapma modu değiştirilemedi"); }
  };

  const setActiveSubMode = async (modeId) => {
    try {
      await axios.post(`${API}/admin/active-mode?profile=${profile}&pricing_mode=${PRICING_MODE}`, { mode: modeId });
      setActiveMode(modeId);
      showMessage("success", `Aktif mod: ${getModeName(modeId)}`);
    } catch (err) { console.error("AK setActiveSubMode error:", err); showMessage("error", "Mod değiştirilemedi"); }
  };

  const addSubMode = async () => {
    const newId = `mod_${Date.now()}`;
    const newMode = { id: newId, name: `Mod ${modes.length + 1}` };
    const updated = [...modes, newMode];
    setModes(updated);
    try {
      await axios.post(`${API}/admin/modes?profile=${profile}&pricing_mode=${PRICING_MODE}`, { modes: updated });
      showMessage("success", `"${newMode.name}" eklendi`);
    } catch (err) { console.error("AK addSubMode error:", err); showMessage("error", "Mod eklenemedi"); }
  };

  const startRename = (mode) => { setRenamingMode(mode.id); setRenameValue(mode.name); };
  const saveRename = async (modeId) => {
    if (!renameValue.trim()) return;
    const updated = modes.map(m => m.id === modeId ? { ...m, name: renameValue.trim() } : m);
    setModes(updated); setRenamingMode(null);
    try {
      await axios.post(`${API}/admin/modes?profile=${profile}&pricing_mode=${PRICING_MODE}`, { modes: updated });
      showMessage("success", "İsim değiştirildi");
    } catch (err) { console.error("AK saveRename error:", err); showMessage("error", "İsim değiştirilemedi"); }
  };

  const deleteSubMode = async (modeId) => {
    if (modes.length <= 1) { showMessage("error", "En az 1 mod olmalı"); return; }
    if (!window.confirm(`"${getModeName(modeId)}" modunu silmek istediğinize emin misiniz?`)) return;
    const updated = modes.filter(m => m.id !== modeId);
    setModes(updated);
    try {
      await axios.delete(`${API}/admin/modes/${modeId}?profile=${profile}&pricing_mode=${PRICING_MODE}`);
      if (activeMode === modeId) {
        const newActive = updated[0]?.id || "mod1";
        await axios.post(`${API}/admin/active-mode?profile=${profile}&pricing_mode=${PRICING_MODE}`, { mode: newActive });
        setActiveMode(newActive);
      }
      if (editingMode === modeId) setEditingMode(updated[0]?.id || "mod1");
      showMessage("success", "Mod silindi");
    } catch (err) { console.error("AK deleteSubMode error:", err); showMessage("error", "Silinemedi"); }
  };

  const getModeName = (id) => modes.find(m => m.id === id)?.name || id;

  if (loading || !carpan || !milyem || !health) {
    return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Yükleniyor...</div>;
  }

  const isAutoMode = carpan.mode === "auto";
  const isActive = pricingModeActive === PRICING_MODE;
  const overallStatus = STATUS_BADGE[health.last_status] || STATUS_BADGE.default;
  const formulaType = carpan.formula_type || "carpan";

  const inputStyle = { width: "100%", padding: "7px 9px", fontSize: "14px", border: "1.5px solid #d0d0d0", borderRadius: 6, fontFamily: "monospace", textAlign: "right", background: "#e4e4e4" };
  const inputDisabled = { ...inputStyle, background: "#f5f5f5", color: "#666", cursor: "not-allowed" };
  const colHeader = { padding: "10px 6px", fontSize: 10, fontWeight: 700, color: "#09301E", textTransform: "uppercase", letterSpacing: 0.4, background: "#fff8ee", borderBottom: "2px solid #DA9F54" };

  return (
    <div style={{ padding: "20px 24px 30px", maxWidth: 1400, margin: "0 auto" }}>
      {success && <p style={styles.success}>{success}</p>}
      {error && <p style={styles.error}>{error}</p>}

      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ color: "var(--adm-canvas-title)", fontSize: 22, margin: 0 }}>Altınkaynak HAS Modu</h2>
          <p style={{ color: "var(--adm-canvas-sub)", fontSize: 13, margin: "4px 0 0" }}>
            QEN HAS (Altınkaynak HAS + marj) × Çarpan + İşçilik + Marj
          </p>
          {aksoStatus && (
            <div data-testid="akso-push-badge" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: aksoStatus.connected && aksoStatus.fresh ? "rgba(22,163,74,0.15)" : "rgba(239,68,68,0.15)",
              color: aksoStatus.connected && aksoStatus.fresh ? "#16a34a" : "#ef4444",
              border: `1px solid ${aksoStatus.connected && aksoStatus.fresh ? "#16a34a" : "#ef4444"}` }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: aksoStatus.connected && aksoStatus.fresh ? "#16a34a" : "#ef4444" }} />
              AKSO {aksoStatus.connected && aksoStatus.fresh ? "canlı" : "bağlantı yok"}
              {aksoStatus.seconds_since != null && (
                <span style={{ fontWeight: 500, opacity: 0.85 }}>• {aksoStatus.seconds_since < 1 ? "şimdi" : `${Math.round(aksoStatus.seconds_since)}sn önce`}</span>
              )}
            </div>
          )}
        </div>
        <button onClick={saveAll} data-testid="ak-save-all-btn"
          style={{ padding: "12px 22px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 2px 8px rgba(22,163,74,0.3)" }}>
          💾 Tümünü Kaydet
        </button>
      </div>

      <HasSourceConfig
        hasSource={hasSource}
        divergenceMode={divergenceMode}
        lastDivergence={lastDivergence}
        autoMarjAdj={autoMarjAdj}
        fallback={fallback}
        ozanHealth={ozanHealth}
        onChangeHasSource={changeHasSource}
        onChangeDivergenceMode={changeDivergenceMode}
      />

      <AkModesManager
        modes={modes}
        activeMode={activeMode}
        editingMode={editingMode}
        renamingMode={renamingMode}
        renameValue={renameValue}
        onSetEditingMode={setEditingMode}
        onSetActiveSubMode={setActiveSubMode}
        onAddSubMode={addSubMode}
        onStartRename={startRename}
        onSaveRename={saveRename}
        onCancelRename={() => setRenamingMode(null)}
        onRenameValueChange={setRenameValue}
        onDeleteSubMode={deleteSubMode}
      />

      {/* Sağlık Durumu */}
      <div style={{ background: "#e4e4e4", borderRadius: 12, padding: 16, marginBottom: 14, border: `2px solid ${overallStatus.color}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, color: "#09301E", display: "flex", alignItems: "center", gap: 8 }}>
              Sarrafiye Çarpanları — {overallStatus.label} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({getModeName(editingMode)})</span>
            </h3>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 5 }}>
              Son: <strong>{fmtTime(health.last_updated)}</strong>
              &nbsp;|&nbsp; Sonraki: <strong>{fmtTime(health.next_update)}</strong>
              &nbsp;|&nbsp; Kalan: <strong>{fmtCountdown(countdown)}</strong>
            </div>
            {health.last_error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>⚠️ {health.last_error}</div>}
          </div>
          <button onClick={refreshNow} disabled={refreshing} data-testid="ak-refresh-now-btn"
            style={{ padding: "10px 20px", fontSize: 13, fontWeight: 700, background: refreshing ? "#94a3b8" : "#DA9F54", color: "#000", border: "none", borderRadius: 8, cursor: refreshing ? "wait" : "pointer" }}>
            {refreshing ? "Çekiliyor..." : "🔄 Şimdi Al"}
          </button>
        </div>
      </div>

      {/* HAS Altın Marjı */}
      <div style={{ background: "#e4e4e4", borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, color: "#09301E", fontSize: 15 }}>💎 HAS Altın Marjı</h3>
          <button onClick={saveHasOnly} data-testid="ak-save-has-btn"
            style={{ padding: "8px 16px", background: "#DA9F54", color: "#000", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            💾 Kaydet
          </button>
        </div>
        <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px" }}>
          QEN HAS = ham HAS + marj. Tüm sarrafiye, gram ve bilezik bu marjlı QEN HAS ile çarpılır.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ fontSize: 11, color: "#e74c3c", fontWeight: 700 }}>ALIŞ MARJ</label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button type="button" data-testid="ak-has-alis-marj-neg"
                onClick={() => updateHasField("alis_marj", String(-(parseFloat(carpan.has_altin?.alis_marj) || 0)))}
                style={{ flexShrink: 0, width: 30, height: 34, border: "1px solid #cbd5e1", borderRadius: 6, background: "#f1f5f9", color: "#334155", fontWeight: 800, fontSize: 16, cursor: "pointer", lineHeight: 1 }}>±</button>
              <input type="text" inputMode="decimal" value={carpan.has_altin?.alis_marj ?? ""} onChange={e => updateHasField("alis_marj", e.target.value)} style={inputStyle} data-testid="ak-has-alis-marj" />
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ fontSize: 11, color: "#27ae60", fontWeight: 700 }}>SATIŞ MARJ</label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button type="button" data-testid="ak-has-satis-marj-neg"
                onClick={() => updateHasField("satis_marj", String(-(parseFloat(carpan.has_altin?.satis_marj) || 0)))}
                style={{ flexShrink: 0, width: 30, height: 34, border: "1px solid #cbd5e1", borderRadius: 6, background: "#f1f5f9", color: "#334155", fontWeight: 800, fontSize: 16, cursor: "pointer", lineHeight: 1 }}>±</button>
              <input type="text" inputMode="decimal" value={carpan.has_altin?.satis_marj ?? ""} onChange={e => updateHasField("satis_marj", e.target.value)} style={inputStyle} data-testid="ak-has-satis-marj" />
            </div>
          </div>
        </div>
      </div>

      <SarrafiyeCarpanTable
        carpan={carpan}
        sarrafiyeList={SARRAFIYE}
        isAutoMode={isAutoMode}
        formulaType={formulaType}
        inputStyle={inputStyle}
        inputDisabled={inputDisabled}
        colHeader={colHeader}
        onToggleMode={toggleMode}
        onSetFormulaType={setFormulaType}
        onUpdateField={updateCarpanField}
        onSave={saveCarpan}
      />

      <MilyemTable
        milyem={milyem}
        milyemProducts={MILYEM_PRODUCTS}
        inputStyle={inputStyle}
        colHeader={colHeader}
        onUpdateField={updateMilyemField}
        onSave={saveMilyem}
      />

      {/* Aktivasyon */}
      <MakasModeCard ref={makasRef} profile={profile} marjMode={editingMode} modeName={modes.find(m => m.id === editingMode)?.name || editingMode} />

      <div style={{ background: "#e4e4e4", borderRadius: 12, padding: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <h3 style={{ margin: "0 0 6px", color: "#09301E", fontSize: 15 }}>Bu Modu Aktif Et</h3>
        <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>
          {isActive
            ? "✅ Bu mod ŞU AN aktif. Müşteri ekranında bu formül çalışıyor."
            : "Aktif etmek için butona bas. Eski Ozan tabanlı modlar bozulmaz."}
        </p>
        <button onClick={activate} disabled={isActive} data-testid="ak-activate-btn"
          style={{ padding: "12px 24px", fontSize: 14, fontWeight: 700, background: isActive ? "#22c55e" : "#e67e22", color: "#fff", border: "none", borderRadius: 8, cursor: isActive ? "default" : "pointer" }}>
          {isActive ? "✓ ALTINKAYNAK MODU AKTİF" : "🚀 ALTINKAYNAK MODUNU AKTİF ET"}
        </button>
      </div>
    </div>
  );
};

export default AltinkaynakCarpanTab;
